import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  Order,
  Model,
  Workshop,
  Technic,
  Firm,
  OrderUnit,
  User,
} from "../types";
import {
  orderService,
  exchangeRateService,
  userService,
} from "../services/dataService";
import ModelModal from "./ModelModal";
import WorkshopModal from "./WorkshopModal";
import TechnicModal from "./TechnicModal";
import FirmPickerField from "./FirmPicker";
import AlertModal from "./AlertModal";
import "./OrderEditModal.css";

interface OrderEditModalProps {
  isOpen: boolean;
  orderId: string;
  onClose: () => void;
  onSave: (updatedOrder: Order) => void;
}

interface Currency {
  code: string;
  name: string;
}

const OrderEditModal: React.FC<OrderEditModalProps> = ({
  isOpen,
  orderId,
  onClose,
  onSave,
}) => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [order, setOrder] = useState<Order | null>(null);
  const [hasChanges, setHasChanges] = useState(false);
  const initialDataRef = useRef<string>("");

  // Form state
  const [formData, setFormData] = useState({
    deadline: "",
    quantity: "1" as string,
    unit: OrderUnit.Adet,
    pieceCount: 1,
    price: "",
    currency: "TRY",
    priority: "",
    note: "",
  });

  // Selection state
  const [selectedModel, setSelectedModel] = useState<Model | null>(null);
  const [selectedWorkshop, setSelectedWorkshop] = useState<Workshop | null>(
    null,
  );
  const [selectedFirm, setSelectedFirm] = useState<Firm | null>(null);
  const [selectedTechnics, setSelectedTechnics] = useState<Technic[]>([]);
  const [selectedModelist, setSelectedModelist] = useState<User | null>(null);

  // Data lists
  const [modelists, setModelists] = useState<User[]>([]);
  const [currencies, setCurrencies] = useState<Currency[]>([]);

  // Modal states
  const [showModelModal, setShowModelModal] = useState(false);
  const [showWorkshopModal, setShowWorkshopModal] = useState(false);
  const [showTechnicModal, setShowTechnicModal] = useState(false);

  // Alert Modal
  const [alertModal, setAlertModal] = useState<{
    isOpen: boolean;
    message: string;
    type: "info" | "success" | "warning" | "error";
  }>({ isOpen: false, message: "", type: "info" });

  // Track changes
  const getFormSnapshot = useCallback(() => {
    return JSON.stringify({
      formData,
      firmId: selectedFirm?.firmId,
      modelId: selectedModel?.modelId,
      workshopId: selectedWorkshop?.workshopId,
      modelistId: selectedModelist?.userId,
      technicIds: selectedTechnics.map((t) => t.technicId).sort(),
    });
  }, [
    formData,
    selectedFirm,
    selectedModel,
    selectedWorkshop,
    selectedModelist,
    selectedTechnics,
  ]);

  useEffect(() => {
    if (!loading && initialDataRef.current) {
      setHasChanges(getFormSnapshot() !== initialDataRef.current);
    }
  }, [getFormSnapshot, loading]);

  // ESC key to close
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape" && isOpen) {
        onClose();
      }
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [isOpen, onClose]);

  useEffect(() => {
    if (isOpen && orderId) {
      setHasChanges(false);
      loadData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, orderId]);

  const loadData = async () => {
    try {
      setLoading(true);

      // Paralel yükleme
      const [orderData, modelistsData, currenciesData] = await Promise.all([
        orderService.getById(orderId),
        userService.getModelists(),
        exchangeRateService.getCurrencies(),
      ]);

      if (!orderData) {
        setAlertModal({
          isOpen: true,
          message: "Sipariş bulunamadı!",
          type: "error",
        });
        return;
      }

      setOrder(orderData);
      setModelists(modelistsData);
      setCurrencies(currenciesData);

      // Form verilerini doldur
      setFormData({
        deadline: orderData.deadline ? orderData.deadline.split("T")[0] : "",
        quantity: orderData.quantity
          ? String(orderData.quantity).replace(".", ",")
          : "1",
        unit: orderData.unit !== undefined ? orderData.unit : OrderUnit.Adet,
        pieceCount: orderData.pieceCount || 1,
        price: orderData.price?.toString() || "",
        currency: orderData.priceCurrency || orderData.currency || "TRY",
        priority: orderData.priority || "",
        note: orderData.note || "",
      });

      // Seçimleri doldur
      setSelectedFirm(orderData.firm || null);
      setSelectedModel(orderData.model || null);
      setSelectedWorkshop(orderData.workshop || null);

      // Modelist
      if (orderData.modelistUserId && modelistsData.length > 0) {
        const modelist = modelistsData.find(
          (m) => m.userId === orderData.modelistUserId,
        );
        setSelectedModelist(modelist || null);
      }

      // Teknikler
      if (orderData.orderTechnics && orderData.orderTechnics.length > 0) {
        const technics = orderData.orderTechnics
          .map((ot) => ot.technic)
          .filter((t): t is Technic => t !== undefined);
        const uniqueTechnics = technics.filter(
          (technic, index, self) =>
            index === self.findIndex((t) => t.technicId === technic.technicId),
        );
        setSelectedTechnics(uniqueTechnics);
      }
    } catch (error) {
      console.error("❌ Failed to load order:", error);
      setAlertModal({
        isOpen: true,
        message: "Sipariş yüklenemedi!",
        type: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  // Set initial snapshot after loading completes
  useEffect(() => {
    if (!loading && order) {
      const snap = getFormSnapshot();
      if (!initialDataRef.current) {
        initialDataRef.current = snap;
      }
    }
  }, [loading, order, getFormSnapshot]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!selectedModel) {
      setAlertModal({
        isOpen: true,
        message: "Lütfen bir model seçin!",
        type: "warning",
      });
      return;
    }

    if (!selectedFirm) {
      setAlertModal({
        isOpen: true,
        message: "Lütfen bir firma seçin!",
        type: "warning",
      });
      return;
    }

    if (!order) return;

    try {
      setSaving(true);

      const orderTechnics = selectedTechnics.map((technic) => ({
        orderTechnicId: undefined,
        orderId: order.orderId,
        technicId: technic.technicId,
        technic: undefined,
      }));

      const updateData = {
        orderId: order.orderId,
        acceptanceDate: order.acceptanceDate,
        completionDate: order.completionDate,
        deadline: formData.deadline
          ? new Date(formData.deadline).toISOString()
          : undefined,
        firmId: selectedFirm.firmId,
        modelId: selectedModel.modelId,
        quantity: parseFloat(formData.quantity.replace(",", ".")) || 1,
        // En/Boy bu modalda düzenlenmiyor ama PUT körlemesine kopyalıyor —
        // taşınmazsa her düzenlemede siliniyorlar.
        width: order.width,
        height: order.height,
        orderUnitId: formData.unit,
        pieceCount:
          formData.unit === OrderUnit.Takim ? formData.pieceCount : undefined,
        price: formData.price ? parseFloat(formData.price) : undefined,
        priceCurrency: formData.currency,
        vatRate: order.vatRate,
        workshopId: selectedWorkshop?.workshopId || undefined,
        operatorId: order.operatorId,
        modelistUserId: selectedModelist?.userId || undefined,
        priority: formData.priority || undefined,
        note: formData.note || undefined,
        invoice: order.invoice,
        invoiceNumber: order.invoiceNumber,
        status: order.status,
        orderStatusId: order.status,
        qrCodeUrl: order.qrCodeUrl,
        createdAt: order.createdAt,
        createdBy: order.createdBy,
        isActive: order.isActive,
        orderTechnics: orderTechnics,
        images: order.images || [],
      };

      await orderService.update(order.orderId, updateData as unknown as Order);

      // Güncellenmiş veriyi parent'a bildir
      const updatedOrder: Order = {
        ...order,
        ...updateData,
        firm: selectedFirm,
        model: selectedModel,
        workshop: selectedWorkshop || undefined,
      };

      onSave(updatedOrder);

      setAlertModal({
        isOpen: true,
        message: "Sipariş başarıyla güncellendi!",
        type: "success",
      });

      // Kısa bir delay sonra kapat
      setTimeout(() => {
        onClose();
      }, 1000);
    } catch (error: any) {
      console.error("❌ Failed to update order:", error);
      setAlertModal({
        isOpen: true,
        message: error.response?.data?.message || "Sipariş güncellenemedi!",
        type: "error",
      });
    } finally {
      setSaving(false);
    }
  };

  const handleSelectTechnic = (technic: Technic) => {
    setSelectedTechnics((prev) => {
      const isAlreadySelected = prev.some(
        (t) => t.technicId === technic.technicId,
      );
      if (isAlreadySelected) {
        return prev.filter((t) => t.technicId !== technic.technicId);
      }
      return [...prev, technic];
    });
  };

  if (!isOpen) return null;

  const totalPrice = formData.price
    ? parseFloat(formData.price) *
      (parseFloat(formData.quantity.replace(",", ".")) || 1)
    : 0;

  return (
    <div className="order-edit-modal-overlay">
      <div className="order-edit-modal" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="order-edit-modal__header">
          <div className="order-edit-modal__header-info">
            <span className="order-edit-modal__icon">
              <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
              </svg>
            </span>
            <div>
              <h2>Sipariş Düzenle</h2>
              {order && (
                <span className="order-edit-modal__order-id">
                  #{order.orderId.substring(0, 8)}
                </span>
              )}
            </div>
          </div>
          <div className="order-edit-modal__header-actions">
            {hasChanges && (
              <span className="order-edit-modal__unsaved-badge">
                Kaydedilmemiş değişiklikler
              </span>
            )}
            <button className="order-edit-modal__close" onClick={onClose} aria-label="Kapat">
              <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <line x1="18" y1="6" x2="6" y2="18" />
                <line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="order-edit-modal__content">
          {loading ? (
            <div className="order-edit-modal__loading">
              <div className="spinner"></div>
              <p>Yükleniyor...</p>
            </div>
          ) : (
            <form onSubmit={handleSubmit}>
              {/* Firma & Model */}
              <div className="order-edit-modal__section">
                <div className="order-edit-modal__section-header">
                  <span className="section-icon">🏢</span>
                  <h3>Firma & Model</h3>
                </div>
                <div className="order-edit-modal__row">
                  <div className="order-edit-modal__field">
                    <label>
                      Firma <span className="required">*</span>
                    </label>
                    <FirmPickerField value={selectedFirm?.firmId || null} firm={selectedFirm} onChange={(f) => setSelectedFirm(f)} placeholder="Firma seçin" />
                  </div>
                  <div className="order-edit-modal__field">
                    <label>
                      Model <span className="required">*</span>
                    </label>
                    <div
                      className={`order-edit-modal__selector ${selectedModel ? "has-value" : ""}`}
                      onClick={() => setShowModelModal(true)}
                    >
                      {selectedModel ? (
                        <span className="selected">
                          {selectedModel.modelCode || selectedModel.modelName}
                        </span>
                      ) : (
                        <span className="placeholder">Model seçin...</span>
                      )}
                      <span className="icon">▼</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Miktar & Fiyat */}
              <div className="order-edit-modal__section">
                <div className="order-edit-modal__section-header">
                  <span className="section-icon">💰</span>
                  <h3>Miktar & Fiyat</h3>
                  {totalPrice > 0 && (
                    <span className="order-edit-modal__total-badge">
                      Toplam:{" "}
                      {totalPrice.toLocaleString("tr-TR", {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      })}{" "}
                      {formData.currency}
                    </span>
                  )}
                </div>
                <div className="order-edit-modal__row order-edit-modal__row--4col">
                  <div className="order-edit-modal__field">
                    <label>Miktar</label>
                    <input
                      type="text"
                      inputMode="decimal"
                      value={formData.quantity}
                      onChange={(e) => {
                        const val = e.target.value;
                        if (val === "" || /^[0-9]*[,.]?[0-9]*$/.test(val)) {
                          setFormData({
                            ...formData,
                            quantity: val,
                          });
                        }
                      }}
                    />
                  </div>
                  <div className="order-edit-modal__field">
                    <label>Birim</label>
                    <select
                      value={formData.unit}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          unit: parseInt(e.target.value),
                        })
                      }
                    >
                      <option value={OrderUnit.Adet}>Adet</option>
                      <option value={OrderUnit.Metre}>Metre</option>
                      <option value={OrderUnit.Takim}>Takım</option>
                      <option value={OrderUnit.Pano}>Pano</option>
                    </select>
                  </div>
                  <div className="order-edit-modal__field">
                    <label>Birim Fiyat</label>
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      value={formData.price}
                      onChange={(e) =>
                        setFormData({ ...formData, price: e.target.value })
                      }
                      placeholder="0.00"
                    />
                  </div>
                  <div className="order-edit-modal__field">
                    <label>Para Birimi</label>
                    <select
                      value={formData.currency}
                      onChange={(e) =>
                        setFormData({ ...formData, currency: e.target.value })
                      }
                    >
                      {currencies.map((c) => (
                        <option key={c.code} value={c.code}>
                          {c.code}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
                {formData.unit === OrderUnit.Takim && (
                  <div
                    className="order-edit-modal__row"
                    style={{ marginTop: 12 }}
                  >
                    <div className="order-edit-modal__field">
                      <label>Parça Sayısı (Takımda)</label>
                      <input
                        type="number"
                        min="1"
                        value={formData.pieceCount}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            pieceCount: parseInt(e.target.value) || 1,
                          })
                        }
                      />
                    </div>
                  </div>
                )}
              </div>

              {/* Atölye & Modelist */}
              <div className="order-edit-modal__section">
                <div className="order-edit-modal__section-header">
                  <span className="section-icon">🏭</span>
                  <h3>Atölye & Desinatör</h3>
                </div>
                <div className="order-edit-modal__row">
                  <div className="order-edit-modal__field">
                    <label>Atölye</label>
                    <div
                      className={`order-edit-modal__selector ${selectedWorkshop ? "has-value" : ""}`}
                      onClick={() => setShowWorkshopModal(true)}
                    >
                      {selectedWorkshop ? (
                        <div className="selector-content">
                          <span className="selected">
                            {selectedWorkshop.name}
                          </span>
                          <button
                            type="button"
                            className="selector-clear"
                            onClick={(e) => {
                              e.stopPropagation();
                              setSelectedWorkshop(null);
                            }}
                            title="Temizle"
                          >
                            ✕
                          </button>
                        </div>
                      ) : (
                        <span className="placeholder">Atölye seçin...</span>
                      )}
                      <span className="icon">▼</span>
                    </div>
                  </div>
                  <div className="order-edit-modal__field">
                    <label>Desinatör</label>
                    <select
                      value={selectedModelist?.userId || ""}
                      onChange={(e) => {
                        const modelist = modelists.find(
                          (m) => m.userId === e.target.value,
                        );
                        setSelectedModelist(modelist || null);
                      }}
                    >
                      <option value="">Seçiniz...</option>
                      {modelists.map((m) => (
                        <option key={m.userId} value={m.userId}>
                          {m.firstName} {m.lastName}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>

              {/* Teknikler */}
              <div className="order-edit-modal__section">
                <div className="order-edit-modal__section-header">
                  <span className="section-icon">🧵</span>
                  <h3>Teknikler</h3>
                  {selectedTechnics.length > 0 && (
                    <span className="order-edit-modal__count-badge">
                      {selectedTechnics.length}
                    </span>
                  )}
                </div>
                <div className="order-edit-modal__technics">
                  {selectedTechnics.length > 0 ? (
                    <div className="order-edit-modal__technic-tags">
                      {selectedTechnics.map((t) => (
                        <span key={t.technicId} className="technic-tag">
                          {t.name}
                          <button
                            type="button"
                            onClick={() => handleSelectTechnic(t)}
                          >
                            ✕
                          </button>
                        </span>
                      ))}
                    </div>
                  ) : (
                    <p className="no-technics">Seçili teknik yok</p>
                  )}
                  <button
                    type="button"
                    className="btn btn-sm btn-primary"
                    onClick={() => setShowTechnicModal(true)}
                  >
                    + Teknik Ekle/Kaldır
                  </button>
                </div>
              </div>

              {/* Tarih & Öncelik */}
              <div className="order-edit-modal__section">
                <div className="order-edit-modal__section-header">
                  <span className="section-icon">📅</span>
                  <h3>Tarih & Öncelik</h3>
                </div>
                <div className="order-edit-modal__row">
                  <div className="order-edit-modal__field">
                    <label>Termin Tarihi</label>
                    <input
                      type="date"
                      value={formData.deadline}
                      onChange={(e) =>
                        setFormData({ ...formData, deadline: e.target.value })
                      }
                    />
                  </div>
                  <div className="order-edit-modal__field">
                    <label>Öncelik</label>
                    <select
                      value={formData.priority}
                      onChange={(e) =>
                        setFormData({ ...formData, priority: e.target.value })
                      }
                      className={
                        formData.priority === "Acil"
                          ? "priority-urgent"
                          : formData.priority === "Yüksek"
                            ? "priority-high"
                            : ""
                      }
                    >
                      <option value="">Normal</option>
                      <option value="Düşük">Düşük</option>
                      <option value="Normal">Normal</option>
                      <option value="Yüksek">Yüksek</option>
                      <option value="Acil">Acil</option>
                    </select>
                  </div>
                </div>
              </div>

              {/* Not */}
              <div className="order-edit-modal__section">
                <div className="order-edit-modal__section-header">
                  <span className="section-icon">📝</span>
                  <h3>Not</h3>
                </div>
                <div className="order-edit-modal__row">
                  <div className="order-edit-modal__field order-edit-modal__field--full">
                    <textarea
                      value={formData.note}
                      onChange={(e) =>
                        setFormData({ ...formData, note: e.target.value })
                      }
                      placeholder="Sipariş notu ekleyin..."
                      rows={3}
                    />
                    <span className="char-count">
                      {formData.note.length} karakter
                    </span>
                  </div>
                </div>
              </div>
            </form>
          )}
        </div>

        {/* Footer */}
        {!loading && (
          <div className="order-edit-modal__footer">
            <div className="order-edit-modal__footer-info">
              {hasChanges && (
                <span className="footer-changes-indicator">
                  ● Değişiklikler kaydedilmedi
                </span>
              )}
            </div>
            <div className="order-edit-modal__footer-actions">
              <button
                type="button"
                className="btn btn-secondary"
                onClick={onClose}
                disabled={saving}
              >
                İptal
              </button>
              <button
                type="submit"
                className="btn btn-primary"
                onClick={handleSubmit}
                disabled={saving || !hasChanges}
              >
                {saving ? (
                  <>
                    <span className="btn-spinner"></span>
                    Kaydediliyor...
                  </>
                ) : (
                  "Kaydet"
                )}
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Sub Modals */}

      <ModelModal
        isOpen={showModelModal}
        onClose={() => setShowModelModal(false)}
        onSelect={(model: Model) => {
          setSelectedModel(model);
          setShowModelModal(false);
        }}
      />

      <WorkshopModal
        isOpen={showWorkshopModal}
        onClose={() => setShowWorkshopModal(false)}
        onSelect={(workshop: Workshop) => {
          setSelectedWorkshop(workshop);
          setShowWorkshopModal(false);
        }}
      />

      <TechnicModal
        isOpen={showTechnicModal}
        onClose={() => setShowTechnicModal(false)}
        onSelectTechnic={handleSelectTechnic}
        selectedTechnics={selectedTechnics}
      />

      {/* Alert Modal */}
      <AlertModal
        isOpen={alertModal.isOpen}
        message={alertModal.message}
        type={alertModal.type}
        onClose={() => setAlertModal({ ...alertModal, isOpen: false })}
      />
    </div>
  );
};

export default OrderEditModal;
